import { Button, Select, TableProps } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import BackButtonIcon from "../../../assets/icons/BackButtonIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import InfoIcon from "../../../assets/icons/InfoIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SettingIcon from "../../../assets/icons/SettingIcon";
import Logo from "../../../assets/images/Sheba-Business-Logo.svg";
import PrintableContent from "../../../components/common/PrintableContent";
import TopCard from "../../../components/common/TopCard";
import AdjustmentLogModal from "../../../components/Leave/LeaveBalance/AdjustmentLogModal";
import EmployeeInfo from "../../../components/Leave/LeaveBalance/EmployeeInfo";
import LeaveAdjustmentModal from "../../../components/Leave/LeaveBalance/LeaveAdjustmentModal";
import LeaveInfo from "../../../components/Leave/LeaveBalance/LeaveInfo";
import CustomTable from "../../../components/ui/CustomTable";
import Loader from "../../../components/ui/Loader";
import { dateFormat, dbDateFormat } from "../../../constants/constants";
import { useGetLeaveApplicationListQuery } from "../../../features/leaveApplication/leaveApplicationApi";
import {
  useGetLeaveBalanceDetailsQuery,
  useLazyDownloadEmployeeLeaveBalanceQuery,
} from "../../../features/leaveProrate/leaveProrateApi";
import { useGetLeaveTypeListQuery } from "../../../features/leaveType/leaveTypeApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import usePrint from "../../../hooks/usePrint";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import {
  GetLeaveApplicationParams,
  GetLeaveBalanceDetailsResponseType,
} from "../../../types";
import { capitalize } from "../../../utils/string";
import {
  addKeyInArray,
  backLink,
  selectArrayOption,
  selectSearchOption,
  skipData,
} from "../../../utils/utils";
import NotFound from "../../NotFound";
import { leaveStatusList } from "./LeaveRequests";

enum Status {
  accepted = "status-success",
  pending = "status-warning",
  rejected = "status-danger",
  canceled = "status-danger",
}

export type LeaveBalanceDetailsFilterType = Omit<
  GetLeaveApplicationParams,
  "limit" | "skip"
>;

export default function LeaveBalanceDetails() {
  const { viewId } = useParams();
  const location = useLocation();
  const { page, limit, handlePagination, handleLimit, resetPagination } =
    useTable();
  const { open, handleClose, handleOpen } = useModal<
    undefined,
    "ADJUST" | "ADJUST_LOG"
  >();
  const [filter, setFilter] = useState<LeaveBalanceDetailsFilterType>();
  const { currentRef } = usePrint("employee_leave_balance.pdf");
  const [
    downloadEmployeeLeaveBalance,
    { isLoading: isDownloading, data: pdfUrl },
  ] = useLazyDownloadEmployeeLeaveBalanceQuery();

  const {
    data: leaveTypeListData,
    isLoading: isLeaveTypeListLoading,
    isError: isLeaveTypeError,
    error: leaveTypeError,
  } = useGetLeaveTypeListQuery({
    params: {
      is_published: 1,
    },
  });

  const { isLoading, isError, error, data } = useGetLeaveBalanceDetailsQuery(
    {
      params: {
        business_member_id: Number(viewId),
      },
    },
    {
      skip: !viewId,
    }
  );

  const {
    isLoading: isLeaveLoading,
    isError: isLeaveError,
    error: leaveError,
    data: leaveData,
  } = useGetLeaveApplicationListQuery(
    {
      params: {
        business_member_id: Number(viewId),
        limit,
        skip: skipData(page, limit),
        ...filter,
      },
    },
    {
      skip: !viewId,
    }
  );

  useError(isError, error, "Get leave balance data fail");
  useError(isLeaveError, leaveError, "Get leave history fail");
  useError(isLeaveTypeError, leaveTypeError, "Get leave type fail");

  const employeeData = data?.data?.employee_info;

  const handleFilter = <K extends keyof LeaveBalanceDetailsFilterType>(
    filterType: K,
    value: LeaveBalanceDetailsFilterType[K]
  ) => {
    setFilter((prev) => ({
      ...prev,
      [filterType]: value,
    }));

    resetPagination();
  };

  const columns: TableProps<
    GetLeaveBalanceDetailsResponseType["data"]["leave_info"]
  >["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Applied Date",
      dataIndex: "created_at",
      render: (val) => (val ? formatDate(val) : "n/a"),
    },
    {
      title: "Leave Duration",
      render: (val) => (
        <div className="max-w-[120px] inline-block leading-[16px]">
          {val?.start_date && val?.end_date ? (
            <>
              <span>
                {dayjs(val?.start_date, dbDateFormat)?.format(dateFormat)}{" "}
              </span>

              <span>
                -<br /> {dayjs(val?.end_date, dbDateFormat)?.format(dateFormat)}
              </span>
            </>
          ) : (
            "n/a"
          )}
        </div>
      ),
    },
    {
      title: "Leave Type",
      dataIndex: "leave_type",
      render: (val) => val?.title || "n/a",
    },
    {
      title: "Leave Days",
      dataIndex: "total_days",
      align: "center",
      render: (val) => (val || 0) + " Days",
    },
    {
      title: "Leave Status",
      dataIndex: "status",
      render: (value: keyof typeof Status) => (
        <div className="flex items-center gap-1">
          <span className={`${Status[value]} min-w-24`}>
            {capitalize(value === "accepted" ? "approved" : value)}
          </span>
          <Button
            className="action-button border-0"
            size="small"
            icon={<InfoIcon />}
          />
        </div>
      ),
    },
  ];

  const handleDownloadLeaveBalanceDetails = () => {
    downloadEmployeeLeaveBalance({
      params: { business_member_id: Number(viewId) },
    });
  };

  useEffect(() => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "employee_leave_balance.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [pdfUrl]);

  if (error && "status" in error && error.status === 404) {
    return <NotFound title="Sorry, data not found" />;
  }

  return (
    <SidebarLayout title="Leave Balance Details">
      {open.type === "ADJUST" && (
        <LeaveAdjustmentModal open={open.open} handleClose={handleClose} />
      )}

      {open.type === "ADJUST_LOG" && (
        <AdjustmentLogModal
          open={open?.open}
          // data={open?.data}
          handleClose={handleClose}
        />
      )}

      <TopCard
        leftChildren={
          <Link to={backLink(location.pathname, 2)}>
            <Button className="normal-dark-button" icon={<BackButtonIcon />}>
              Back
            </Button>
          </Link>
        }
      >
        <Button
          type="primary"
          onClick={() =>
            handleOpen({
              type: "ADJUST",
            })
          }
        >
          Leave Adjustment
        </Button>
        <Button
          type="text"
          className="!text-primary border-primary"
          onClick={() =>
            handleOpen({
              type: "ADJUST_LOG",
            })
          }
        >
          Adjustment Log
        </Button>
        <Button
          className="download-button"
          icon={<DownloadIcon />}
          loading={isDownloading}
          onClick={handleDownloadLeaveBalanceDetails}
        >
          Export Report
        </Button>
        <Link to="/admin/settings/leave/leave-prorate">
          <Button
            type="primary"
            className="middle-button"
            icon={<SettingIcon />}
          />
        </Link>
      </TopCard>

      {isLoading ? (
        <div className="mb-6">
          <Loader />
        </div>
      ) : (
        <div className="grid md:grid-cols-8 gap-6 mb-6">
          <EmployeeInfo
            name={employeeData?.name}
            email={employeeData?.email}
            department={employeeData?.department}
            designation={employeeData?.designation}
            joinedDate={employeeData?.joined_date}
            image={employeeData?.profile_image}
            employeeId={employeeData?.employee_id}
          />
          <LeaveInfo data={data?.data?.leave_info} />
        </div>
      )}

      <CustomTable
        loading={isLeaveLoading}
        page={page}
        limit={limit}
        columns={columns}
        dataSource={addKeyInArray(leaveData?.data?.leave_applications)}
        total={leaveData?.data?.total_leave_applications}
        className="header-table-color"
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Select
              allowClear
              showSearch
              onChange={(val) => handleFilter("leave_type_id", val)}
              placeholder="Leave Type"
              filterOption={selectSearchOption}
              options={
                selectArrayOption(
                  leaveTypeListData?.data?.leave_types,
                  "title",
                  "id"
                ) || []
              }
              className="min-w-[140px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              loading={isLeaveTypeListLoading}
            />
            <Select
              allowClear
              showSearch
              onChange={(val) => handleFilter("status", val)}
              placeholder="Leave Status"
              filterOption={selectSearchOption}
              options={leaveStatusList}
              className="min-w-[90px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
          </>
        }
      />

      <PrintableContent ref={currentRef}>
        <div className="mt-4 mb-4 p-6 bg-white text-black">
          <div className="w-full">
            <table className="w-full border-collapse">
              <tr className="align-middle flex items-center pb-1 border-b border-gray-50">
                <td className="w-1/5 p-2 border-none">
                  <img src={Logo} alt="logo" className="w-24" />
                </td>
                <td className="w-4/5 p-2 text-left border-none">
                  <h3 className="m-0 text-right text-[20px] font-normal">
                    Fleet Fun Sheba
                  </h3>
                </td>
              </tr>
            </table>
          </div>

          <section className="mt-6">
            <h1 className="text-2xl font-normal">Employee Leave Balance</h1>

            <div>
              <table className="w-full border-collapse mt-8">
                <tbody>
                  <tr>
                    <td className="w-[50%] align-top border-none">
                      <table className="w-full border-collapse">
                        <tbody>
                          <tr>
                            <td className="w-2/5 p-2 border-none">
                              Employee ID
                            </td>
                            <td className="w-3/5 p-2 border-none text-[16px]">
                              <span className="mr-2 font-normal">:</span>{" "}
                              {employeeData?.employee_id || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td className="p-2 border-none">Employee Name</td>
                            <td className="p-2 border-none text-[16px]">
                              <span className="mr-2 font-bold">:</span>{" "}
                              {employeeData?.name || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td className="p-2 border-none">Department</td>
                            <td className="p-2 border-none text-[16px]">
                              <span className="mr-2 font-normal">:</span>{" "}
                              {employeeData?.department || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td className="p-2 border-none">Designation</td>
                            <td className="p-2 border-none text-[16px]">
                              <span className="mr-2 font-normal">:</span>{" "}
                              {employeeData?.designation || "N/A"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>

                    <td className="w-[50%] align-top text-start border-none">
                      <table className="w-full border border-gray-300">
                        <thead>
                          <tr className="bg-gray-200">
                            <th
                              colSpan={2}
                              className="w-[100%] p-2 pb-4 text-center !bg-[#f2f3f4] border border-gray-200"
                            >
                              Leave summary
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {data?.data?.leave_info?.leave_balances?.map(
                            (item, index) => (
                              <tr key={index}>
                                <td className="w-[75%] p-2 pb-4 border border-gray-200">
                                  {item?.leave_type || "N/A"}
                                </td>
                                <td className="w-[25%] p-2 pb-4 border border-gray-200">
                                  {item?.remaining_days || "N/A"}/
                                  {item?.total_days || "N/A"}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-20">
            <h3 className="text-lg font-bold mt-4 mb-4">Leave Details</h3>
            <table className="w-full border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="w-[25%] p-2 pb-4 text-center !bg-[#f2f3f4] border border-gray-200">
                    Created at
                  </th>
                  <th className="w-[25%] p-2 pb-4 text-center !bg-[#f2f3f4] border border-gray-200">
                    Leave Type
                  </th>
                  <th className="w-[35%] p-2 pb-4 text-center !bg-[#f2f3f4] border border-gray-200">
                    Leave Days
                  </th>
                  <th className="w-[25%] p-2 pb-4 text-center !bg-[#f2f3f4] border border-gray-200">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {leaveData?.data?.leave_applications?.map((item, index) => (
                  <tr key={index}>
                    <td className="p-2 pb-4 border border-gray-300">
                      {formatDate(item?.created_at || "N/A")}
                    </td>
                    <td className="p-2 pb-4 border border-gray-300">
                      {item?.leave_type?.title || "N/A"}
                    </td>
                    <td className="p-2 pb-4 border border-gray-300">
                      {item?.total_days || "N/A"}
                    </td>
                    <td className="p-2 pb-4 border border-gray-300">
                      {item?.status || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      </PrintableContent>
    </SidebarLayout>
  );
}
